import React, { useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import { DialogHeader } from "@/shared/components/DialogHeader";
import { tShared } from "@/shared/i18n";
import { FormBuilder } from "../FormBuilder";
import type { CustomFieldsSchemaItem } from "../types";
import type { Schema } from "../types";
import { ArrayItemRow } from "../ArrayItemRow";

// Бэкенд хранит значение поля по этому имени и проверяет его тем же
// выражением (см. TagTypeValidator / PlatformValidator): без клиентской
// проверки ошибка всплывает только после сохранения.
const FIELD_NAME_PATTERN = /^[a-z0-9_]+$/;

function validate(values: unknown): Record<string, any> {
  const errors: Record<string, any> = {};
  const name = String((values as Record<string, any>)?.name ?? "").trim();

  if (name && !FIELD_NAME_PATTERN.test(name)) {
    errors.name = tShared(
      "shared.customFieldsSchema.nameInvalid",
      "Имя поля может содержать только строчные буквы, цифры и подчеркивания"
    );
  }

  return errors;
}

const DEFAULT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { label: "Текст", value: "TEXT" },
  { label: "Число", value: "NUMBER" },
  { label: "Булево", value: "BOOLEAN" },
  { label: "Дата", value: "DATE" },
  { label: "Выбор", value: "SELECT" },
  { label: "Страна", value: "COUNTRY" },
];

export interface CustomFieldsSchemaDialogProps {
  open: boolean;
  onClose: () => void;
  initialValue: Partial<CustomFieldsSchemaItem> | null;
  onSave: (item: CustomFieldsSchemaItem) => void;
  typeOptions?: { value: string; label: string }[];
}

export const CustomFieldsSchemaDialog: React.FC<
  CustomFieldsSchemaDialogProps
> = ({
  initialValue,
  open,
  onClose,
  onSave,
  typeOptions = DEFAULT_TYPE_OPTIONS,
}) => {
  const initial = useMemo(() => {
    return {
      name: initialValue?.name ?? "",
      type: initialValue?.type ?? "TEXT",
      options: initialValue?.options ?? [],
      multiple: initialValue?.multiple ?? false,
      placeholder: initialValue?.placeholder ?? "",
      helperText: initialValue?.helperText ?? "",
      label: initialValue?.label ?? "",
      required: initialValue?.required ?? false,
    };
  }, [initialValue]);

  const schema: Schema = useMemo(
    () => ({
      name: {
        type: "text",
        label: "Код",
        required: true,
        xs: 12,
        fullWidth: true,
      },
      type: {
        type: "select",
        label: "Тип",
        required: true,
        xs: 12,
        fullWidth: true,
        options: typeOptions,
      },
      options: {
        type: "array",
        label: "Опции выбора",
        addText: "Добавить опцию",
        xs: 12,
        subschema: {
          value: {
            type: "text" as const,
            variant: "filled",
            label: "Значение",
            required: true,
            xs: 6,
            fullWidth: true,
          },
          label: {
            type: "text" as const,
            variant: "standard",
            label: "Подпись",
            xs: 6,
            fullWidth: true,
          },
        },
        itemComponent: ArrayItemRow,
        getNewItem: () => ({ value: "", label: "" }),
        visibleCheck: ({ values }: { values: Record<string, any> }) =>
          values.type === "SELECT",
      },
      multiple: {
        type: "switch",
        label: "Множественный выбор",
        xs: 12,
        fullWidth: true,
        visibleCheck: ({ values }: { values: Record<string, any> }) =>
          values.type === "SELECT",
      },
      label: {
        type: "text",
        label: "Подпись",
        xs: 12,
        fullWidth: true,
      },
      // placeholder: {
      //   type: "text",
      //   label: "Placeholder",
      //   xs: 12,
      //   fullWidth: true,
      // },
      // helperText: {
      //   type: "text",
      //   label: "Подсказка",
      //   xs: 12,
      //   fullWidth: true,
      // },
      required: {
        type: "switch",
        label: "Обязательное поле",
        xs: 12,
        fullWidth: true,
      },
    }),
    [typeOptions]
  );

  const handleSubmit = (values: any) => {
    const item: CustomFieldsSchemaItem = {
      name: values.name?.trim() || "",
      type: values.type || "TEXT",
      label: values.label?.trim() || "",
      placeholder: values.placeholder?.trim() || undefined,
      helperText: values.helperText?.trim() || undefined,
      required: Boolean(values.required),
    };
    if (values.type === "SELECT") {
      item.options = (values.options || [])
        .filter((o: any) => o && (o.value != null || o.label != null))
        .map((o: any) => ({
          value: String(o.value ?? "").trim(),
          label: String(o.label ?? "").trim(),
        }));
      item.multiple = Boolean(values.multiple);
    }
    onSave(item);
    onClose();
  };

  return (
    <Dialog maxWidth="sm" fullWidth open={open} onClose={onClose}>
      <DialogHeader
        title="Настройки поля"
        onClose={onClose}
        actions={
          <Button
            type="submit"
            form="custom-fields-schema-dialog-form"
            variant="contained"
            color="primary"
          >
            Сохранить
          </Button>
        }
      />
      <DialogContent sx={{ p: 1 }}>
        <FormBuilder
          formId="custom-fields-schema-dialog-form"
          schema={schema}
          initialValues={initial}
          onSubmit={handleSubmit}
          validate={validate}
          submitButton={false}
          resetButton={false}
          spacing={2}
          paper
        />
      </DialogContent>
    </Dialog>
  );
};
